import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { createThread } from "../../utils/openAI-utils";
import { upload } from "../../utils/media-utils";
import aiAgents from "../../singleton/AIAgents";
import { resolveRoomUserId } from "../../utils/room-persistence-utils";

let uniqueMessageId = 0;
let uniqueThreadId = 0;
const pmId = () => `pm-${uniqueMessageId++}`; // "pm-" prefix guarantees no collision with public chat numeric IDs
const ptId = () => `pt-${uniqueThreadId++}`;

export const PrivateChatContext = createContext({ messageGroups: [], sendMessage: () => {} });

function chatClientMessageId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

function dialogClient() {
  return typeof window !== "undefined" ? window.APP?.dialog : null;
}

function debugPrivateChatStream(stage, payload) {
  try {
    if (window.localStorage?.getItem("debugAIPrivateChatStream") !== "true") {
      return;
    }
  } catch (error) {
    return;
  }

  const printable = Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, typeof value === "string" ? debugString(value) : value])
  );
  console.debug("[AI private chat stream]", stage, printable);
}

function debugString(value) {
  const chars = Array.from(value);

  return {
    raw: value,
    visible: value.replace(/ /g, "·").replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t"),
    length: value.length,
    headCodes: chars.slice(0, 12).map(char => char.charCodeAt(0)),
    tailCodes: chars.slice(-12).map(char => char.charCodeAt(0))
  };
}

function isPlayableAgentResponse(body) {
  if (!body?.agentUuid) {
    return false;
  }

  const agent = aiAgents.getAgentById(body.agentUuid);
  return Boolean(agent && agent.voiceSpeechStrategy !== "Silent");
}

function processPrivateChatMessage(privateMessageGroups, newMessage, mySessionId) {
  const now = Date.now();
  const { name, sent, sender, sessionId, ...messageProps } = newMessage;
  const clientMessageId = messageProps?.body?.clientMessageId;
  const incomingPlayable = isPlayableAgentResponse(messageProps?.body);
  const messageType = messageProps?.body?.privacyType || newMessage.type;

  if (clientMessageId) {
    const existingGroupIndex = privateMessageGroups.findIndex(group =>
      group.messages?.some(message => message?.body?.clientMessageId === clientMessageId)
    );
    debugPrivateChatStream("final-message-received", {
      clientMessageId,
      matched: existingGroupIndex !== -1,
      message: messageProps?.body?.message || "",
      messageLength: messageProps?.body?.message?.length || 0
    });

    if (existingGroupIndex !== -1) {
      return privateMessageGroups.map((group, index) => {
        if (index !== existingGroupIndex) {
          return group;
        }

        const [message, ...remainingMessages] = group.messages;
        const currentBody = message?.body && typeof message.body === "object" ? message.body : { message: message?.body };
        return {
          ...group,
          loading: false,
          timestamp: now,
          messages: [
            {
              ...message,
              timestamp: now,
              body: {
                ...currentBody,
                ...(messageProps?.body || {}),
                type: messageType
              },
              type: messageType,
              play: messageProps?.body?.play ?? message?.play,
              loadingAudio: messageProps?.body?.loadingAudio ?? message.loadingAudio,
              read: mySessionId === messageProps?.body?.senderSessionId,
              playable: messageProps?.body?.playable ?? (incomingPlayable ? true : message?.playable)
            },
            ...remainingMessages
          ]
        };
      });
    }
  }

  // Find the thread for this sender and receiver
  // const threadId = `${receiver}`;
  // let thread = privateMessageGroups.find(group => group.id === threadId);
  let privateMessageGroup = {
    id: ptId(),
    timestamp: now,
    sent: mySessionId === messageProps?.body?.senderSessionId,
    sender: mySessionId === messageProps?.body?.senderSessionId ? "You" : messageProps?.body?.sender,
    senderSessionId: messageProps?.body?.senderSessionId || sessionId,
    receiver: messageProps?.body?.receiver || receiver,
    messages: [],
    type: messageType,
    loading: messageProps?.body?.loading
  };
  privateMessageGroups.push(privateMessageGroup);
  // Add the new message to the thread
  privateMessageGroup.messages.push({
    id: pmId(),
    timestamp: now,
    body: messageProps?.body?.message || messageProps?.body,
    type: messageType,
    play: messageProps?.body?.play,
    loadingAudio: messageProps?.body?.loadingAudio,
    read: mySessionId === messageProps?.body?.senderSessionId,
    playable: messageProps.body.playable ?? incomingPlayable
  });

  return [...privateMessageGroups];
}

function processEditPrivateMessage(privateMessageGroups, newMessage, receiver, messageId) {
  const { name, sent, sender, sessionId, groupId, ...messageProps } = newMessage;
  let messageGroupId = groupId !== undefined && groupId !== null ? groupId : newMessage?.body?.groupId;
  if (messageGroupId !== undefined && messageGroupId !== null) {
    return [
      ...privateMessageGroups.map(group => {
        if (group.id === messageGroupId) {
          const currentBody = group.messages[0].body;
          const currentBodyObject = currentBody && typeof currentBody === "object" ? currentBody : { message: currentBody };
          const nextBody = messageProps?.body || {};
          group.messages[0].body =
            nextBody.clientMessageId || (currentBody && typeof currentBody === "object")
              ? { ...currentBodyObject, ...nextBody, type: newMessage.type }
              : nextBody.message || currentBody;
          group.loading = messageProps?.body?.loading;
          // group.play = messageProps?.body?.play;
          group.messages[0].play = messageProps?.body?.play ?? group.messages[0].play;
          group.messages[0].audio = messageProps?.body?.audio ?? group.messages[0].audio;
          group.messages[0].playable = messageProps?.body?.playable ?? group.messages[0].playable;
          group.messages[0].loadingAudio = messageProps?.body?.loadingAudio ?? group.messages[0].loadingAudio;
        }

        return { ...group };
      })
    ];
  }

  // return [
  //   ...privateMessageGroups.map(group => {
  //     if (group.id === uniqueThreadId - 1) {
  //       group.messages[0].body = messageProps?.body?.message;
  //       group.loading = messageProps?.body?.loading;
  //       // group.play = messageProps?.body?.play;
  //       group.messages[0].play = messageProps?.body?.play;
  //       group.messages[0].audio = messageProps?.body?.audio;
  //     }

  //     return { ...group };
  //   })
  // ];
}

function addMessage({ newMessage, setPrivateMessageGroups, mySessionId, setUnreadMessages }) {
  // evaluate socket or local message
  const type = newMessage?.body?.privacyType;
  const sessionId = mySessionId || window.mySessionId;

  if (type == "private-chat" && newMessage?.body?.receiver === sessionId) {
    setUnreadMessages(true);
  }

  switch (type) {
    case "private-chat":
      return setPrivateMessageGroups(messages => processPrivateChatMessage(messages, newMessage, mySessionId));
  }
}

function editMessage({ newMessage, setPrivateMessageGroups, receiver, thread, messageId, groupId }) {
  switch (newMessage.type) {
    case "private-chat":
      return setPrivateMessageGroups(messages =>
        processEditPrivateMessage(messages, newMessage, receiver, thread, messageId, groupId)
      );
  }
}

function removeMessageGroup({ id, setPrivateMessageGroups }) {
  setPrivateMessageGroups(groups => deletePrivateMessageGroup(groups, id));
}

function deletePrivateMessageGroup(privateMessageGroups, id) {
  return privateMessageGroups.filter(group => group.id !== id);
}

function cancelPlayPrivateMessages(setPrivateMessageGroups) {
  return setPrivateMessageGroups(
    messageGroups =>
      (messageGroups = messageGroups.map(messageGroup => {
        messageGroup.messages = messageGroup.messages.map(message => {
          message.play = false;
          message.loadingAudio = false;
          return message;
        });
        return messageGroup;
      }))
  );
}

export function PrivateChatContextProvider({ messageDispatch, children, mySessionId }) {
  const [privateMessageGroups, setPrivateMessageGroups] = useState([]);
  const [receiver, setReceiver] = useState(null);
  const [thread, setOpenAIThread] = useState(null);
  const [tabs, setTabs] = useState(["PUBLIC", "PRIVATE"]);
  const [aiIds, setAiIds] = useState([]);
  const [chatUser, setChatUser] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [sendingAIMessage, setSendingAIMessage] = useState(null);

  const privateMessagesRef = useRef();
  useEffect(() => {
    privateMessagesRef.current = privateMessageGroups;
  }, [privateMessageGroups]);

  const cancelAllPlay = useCallback(() => {
    cancelPlayPrivateMessages(setPrivateMessageGroups);
    const agent = aiAgents.getAgentById(receiver);
    aiAgents.cancelAllPlay(agent);
  }, [setPrivateMessageGroups, receiver]);

  async function createNPCThread(npcAiComponent) {
    const threadId = await createThread(npcAiComponent?.apiKey);
    setOpenAIThread(threadId);
    npcAiComponent.threadId = threadId;
  }

  const sendMessageSpawn = useCallback(
    async (file, receiver, sessionId) => {
      if (messageDispatch) {
        const response = await upload(file, "image/png");

        window.APP.store.update({
          uploadPromotionTokens: [{ fileId: response.file_id, promotionToken: response.meta.promotion_token }]
        });
        const url = response.origin + `?token=${response.meta.access_token}`;
        const me = window?.APP?.store?.state?.profile.displayName;
        let eventType = "object";

        if (file?.type?.startsWith("image/")) {
          eventType = "photo";
        } else if (file?.type?.startsWith("video/")) {
          eventType = "video";
        } else if (file?.type?.startsWith("audio/")) {
          eventType = "audio";
        }

        messageDispatch.push(
          {
            name: file.name,
            src: url,
            canSpawn: true,
            receiver,
            sender: me,
            sent: true,
            senderSessionId: sessionId,
            privacyType: "private-chat"
          },
          eventType
        );
      }
    },
    [messageDispatch]
  );

  useEffect(() => {
    function onReceiveMessage(event) {
      const newMessage = event.detail;
      addMessage({ newMessage, setPrivateMessageGroups, receiver, thread, mySessionId, setUnreadMessages });
    }

    function onEditMessage(event) {
      const newMessage = event.detail;
      const { messageId, groupId } = newMessage;
      editMessage({ newMessage, setPrivateMessageGroups, receiver, thread, messageId });
    }

    function onRemoveMessage(event) {
      const newMessage = event.detail;
      const { id } = newMessage;
      removeMessageGroup({ id, setPrivateMessageGroups });
    }

    function onCancelPlay() {
      cancelPlayPrivateMessages(setPrivateMessageGroups);
    }

    if (messageDispatch) {
      messageDispatch.mySessionId = mySessionId;
      messageDispatch.addEventListener("message", onReceiveMessage);
      messageDispatch.addEventListener("ai-local-message", onReceiveMessage);
      messageDispatch.addEventListener("ai-local-message-edit", onEditMessage);
      messageDispatch.addEventListener("ai-cancel-play", onCancelPlay);
      messageDispatch.addEventListener("ai-remove-message", onRemoveMessage);
    }

    return () => {
      if (messageDispatch) {
        messageDispatch.removeEventListener("message", onReceiveMessage);
        messageDispatch.removeEventListener("ai-local-message", onReceiveMessage);
        messageDispatch.removeEventListener("ai-local-message-edit", onEditMessage);
        messageDispatch.removeEventListener("ai-cancel-play", onCancelPlay);
        messageDispatch.removeEventListener("ai-remove-message", onRemoveMessage);
      }
    };
  }, [messageDispatch, setPrivateMessageGroups, receiver, thread, mySessionId]);

  const sendMessage = useCallback(
    message => {
      if (messageDispatch) {
        messageDispatch.dispatch(message);
      }
    },
    [messageDispatch]
  );

  const sendPrivateMessage = useCallback(
    async (message, receiver, sessionId) => {
      if (messageDispatch) {
        const me = window?.APP?.store?.state?.profile.displayName;
        const dialog = dialogClient();
        if (dialog?.sendPrivateChatMessage) {
          const senderUserId = resolveRoomUserId({
            store: window.APP?.store
          });
          try {
            await dialog.sendPrivateChatMessage({
              text: message,
              recipientSessionId: receiver,
              clientMessageId: chatClientMessageId("private-chat"),
              senderUserId,
              metadata: {
                source: "metaverse-private-chat",
                senderSessionId: sessionId,
                senderName: me
              }
            });
          } catch (error) {
            console.warn("[PrivateChat] Failed to send private chat through dialog", error);
          }
          return;
        }

        messageDispatch.push(
          { message, receiver, sender: me, sent: true, senderSessionId: sessionId, privacyType: "private-chat" },
          "chat"
        );
      }
    },
    [messageDispatch]
  );

  const sendAIPrivateMessage = useCallback(
    async (message, receiver, sessionId = "") => {
      if (messageDispatch) {
        const agent = aiAgents.getAgentById(receiver);
        const type = "private-chat";
        const turnId = chatClientMessageId("agent-turn");
        const userClientMessageId = `${turnId}:user`;
        const agentResponseClientMessageId = `${turnId}:agent`;

        messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message", {
            detail: {
              type,
              body: {
                message,
                receiver,
                sender: "You",
                sent: true,
                privacyType: "private-chat",
                senderSessionId: sessionId,
                clientMessageId: userClientMessageId
              }
            }
          })
        );

        aiAgents.setAILoadingMessage(receiver, agentResponseClientMessageId);
        try {
          setSendingAIMessage(true);
          await new Promise(resolve => setTimeout(resolve, 100));
          const AILastMessage = getAILastMessage(receiver, true);
          await aiAgents.sendPrompt({ agent, message, enableSpeech: false, messageId: AILastMessage.id, turnId });
          setSendingAIMessage(false);
        } catch (e) {
          new CustomEvent("ai-message", {
            detail: { type: "private-chat", body: "There was an error sending the message", receiver }
          });
          console.error(e);
          setSendingAIMessage(false);
        }
      }
    },
    [messageDispatch]
  );

  const readAllMesages = useCallback(
    receiver => {
      return setPrivateMessageGroups(
        messageGroups =>
          (messageGroups = messageGroups.map(messageGroup => {
            messageGroup.messages = messageGroup.messages.map(message => {
              if (messageGroup.receiver === mySessionId && messageGroup.senderSessionId === receiver) {
                message.read = true;
              }
              return message;
            });
            return messageGroup;
          }))
      );
    },
    [setPrivateMessageGroups, mySessionId]
  );

  const getAILastMessage = (receiver, loading = false) => {
    let groupMessages = privateMessagesRef.current.filter(messageGroup => messageGroup.receiver == receiver);
    if (loading) {
      groupMessages = groupMessages.filter(group => group.loading === loading);
    }
    const message = groupMessages[groupMessages.length - 1];
    return message;
  };

  useEffect(() => {
    window.getAILastMessage = getAILastMessage;
    // returns {loading, messages[], receiver, sender}
    // each message contains {id,audio, play,read, type: "private-chat", body: {message,receiver,sender,loading, privacyType}}
  }, []);

  return (
    <PrivateChatContext.Provider
      value={{
        sendMessage,
        setPrivateMessageGroups,
        privateMessageGroups,
        sendPrivateMessage,
        setReceiver,
        receiver,
        thread,
        setOpenAIThread,
        createNPCThread,
        cancelAllPlay,
        tabs,
        setTabs,
        chatUser,
        setChatUser,
        sendMessageSpawn,
        sendAIPrivateMessage,
        privateUnreadMessages: unreadMessages,
        setPrivateUnreadMessages: setUnreadMessages,
        aiIds,
        setAiIds,
        readAllMesages,
        sendingAIMessage,
        getAILastMessage
      }}
    >
      {children}
    </PrivateChatContext.Provider>
  );
}

PrivateChatContextProvider.propTypes = {
  children: PropTypes.node,
  messageDispatch: PropTypes.object
};
